import { Machine, assign } from 'xstate'
import { search } from 'api'
import { UserType } from 'types'

interface Context {
  query?: string
  userType?: UserType
  size: number
  total?: number
  results?: any[]
  error?: string
}

type Events = { type: 'SEARCH_CHANGE' }

const stateChart = {
  id: 'search',
  context: {
    query: '',
    userType: undefined,
    size: 30,
    total: 0,
    results: [],
    error: undefined,
  },
  initial: 'idle',
  states: {
    idle: {
      entry: ['setQuery', 'setUserType'],
    },
    debouncing: {
      entry: ['setQuery', 'setUserType'],
      after: {
        300: 'searching',
      },
    },
    searching: {
      invoke: {
        id: 'getSearchResults',
        src: 'fetchResults',
        onDone: {
          target: 'successful',
          actions: ['setResults'],
        },
        onError: {
          target: 'failed',
          actions: ['setError'],
        },
      },
    },
    successful: {
      always: [
        {
          cond: 'noResults',
          target: 'noResults',
        },
        {
          target: 'hasResults',
        },
      ],
    },
    failed: {},
    hasResults: {},
    noResults: {},
  },
  on: {
    SEARCH_CHANGE: [
      {
        cond: 'isSearchEmpty',
        target: 'idle',
        actions: ['resetResults'],
      },
      {
        cond: 'isQueryValid',
        target: 'debouncing',
      },
    ],
  },
}

const config = {
  actions: {
    setResults: assign((__context: any, event: any) => ({
      results: event.data.results,
      total: event.data.total,
    })),
    setQuery: assign((__context: any, event: any) => ({
      query: event.query,
    })),
    setUserType: assign((__context: any, event: any) => ({
      userType: event.userType,
    })),
    resetResults: assign((__context: any, event: any) => ({
      results: [],
      total: 0,
    })),
    setError: assign((__context: any, event: any) => ({
      error: event.data,
    })),
  },
  guards: {
    isQueryValid: (__context: any, event: any) => {
      return event.query.length > 1
    },
    isSearchEmpty: (__context: any, event: any) => {
      return !event.query || event.query.length === 0 || event.query.length < 2
    },
    noResults: (__context: any, event: any) => {
      return __context.total === 0
    },
  },
  services: {
    fetchResults: (__context: any) => {
      return search({
        query: __context.query,
        userType: __context.userType,
        size: __context.size,
      })
    },
  },
}

export const searchMachine = Machine<Context, Events>(stateChart, config)
